import React, { useState, useEffect } from "react";
import swal from "sweetalert";
import {
  getGovtOrders,
  deleteGovtOrder,
  toggleGovtOrderStatus,
} from "../../../services/adminApiService/govtOrderApi/govtOrderApi";
import GovtOrderModal from "./GovtOrderModal";
import PaginationComp from "../../../common/ui/view/pagination/PaginationComp";

const GovtOrderList = () => {
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData(currentPage, search);
  }, [currentPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchData(1, search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  async function fetchData(page, searchTerm) {
    setLoading(true);
    try {
      const res = await getGovtOrders(page, searchTerm);
      setTotalPages(res?.data?.data?.last_page);
      setData(res?.data?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This will permanently delete the government order and its file.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await deleteGovtOrder(id);
          swal("Deleted!", "Government order deleted.", "success");
          fetchData(currentPage, search);
        } catch {
          swal("Error", "Failed to delete.", "error");
        }
      }
    });
  };

  const handleToggleStatus = async (id) => {
    try {
      await toggleGovtOrderStatus(id);
      fetchData(currentPage, search);
    } catch {
      swal("Error", "Failed to update status.", "error");
    }
  };

  const handleModalClose = (refresh = false) => {
    setShowModal(false);
    setSelectedOrder(null);
    if (refresh) fetchData(currentPage, search);
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Government Orders</h4>
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelectedOrder(null);
            setShowModal(true);
          }}
        >
          + Add Government Order
        </button>
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead style={{ backgroundColor: "rgba(12,46,94,1)", color: "#fff" }}>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Date</th>
              <th>File</th>
              <th>Size</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">
                  No government orders found.
                </td>
              </tr>
            ) : (
              data.map((order, idx) => (
                <tr key={order.id}>
                  <td>{(currentPage - 1) * 10 + idx + 1}</td>
                  <td>{order.title}</td>
                  <td>
                    {order.order_date
                      ? new Date(order.order_date).toLocaleDateString("en-IN")
                      : "—"}
                  </td>
                  <td>
                    {order.file_url && (
                      <a
                        href={order.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        View PDF
                      </a>
                    )}
                  </td>
                  <td>{order.file_size || "—"}</td>
                  <td>
                    <span
                      className={`badge ${
                        order.status ? "bg-success" : "bg-secondary"
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleToggleStatus(order.id)}
                      title="Click to toggle"
                    >
                      {order.status ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-warning me-2"
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowModal(true);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(order.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <PaginationComp
          size={totalPages}
          currentpage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}

      {showModal && (
        <GovtOrderModal order={selectedOrder} onClose={handleModalClose} />
      )}
    </div>
  );
};

export default GovtOrderList;
